import React from 'react';
import PropTypes from 'prop-types';
import { Form, Icon, Input, Modal } from 'sugar-design';

import { PWD_MIN_LENGTH } from '@/constants/common';
import {
  isContainLowerCase,
  isContainNumber,
  isContainSpecialChar,
  isContainUpperCase,
} from '@/utils/common';
import { getI18n } from '@/utils/i18n';

import styles from './index.cm.styl';

const i18n = getI18n();

const initState = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
  isSavingPassword: false,
  errorMessage: '',
  oldPwdErrMsg: '',
  confirmPwdErrMsg: '',
  showStrengthCheck: false,
  geMinLength: false, // 大于等于密码最小长度。
  geMinLengthError: false,
  containError: false,
  containNumber: false,
  containUpperCase: false,
  containLowerCase: false,
  containSpecialChar: false,
  diffCurrentPwd: false, // 与当前密码不相同。
  diffCurrentError: false,
};

class ChangePassword extends React.Component {
  constructor(props) {
    super(props);
    this.state = initState;
    this.onOldPasswordChange = this.onOldPasswordChange.bind(this);
    this.onNewPasswordChange = this.onNewPasswordChange.bind(this);
    this.onConfirmPasswordChange = this.onConfirmPasswordChange.bind(this);
    this.showStrengthCheck = this.showStrengthCheck.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.renderStrengthCheck = this.renderStrengthCheck.bind(this);
  }

  onOldPasswordChange(e) {
    const { newPassword } = this.state;
    const oldPassword = e.target.value;
    let oldPwdErrMsg = '';
    if (!oldPassword) {
      oldPwdErrMsg = i18n.t`原始密码不能为空`;
    }
    if (newPassword) {
      if (oldPassword === newPassword) {
        this.setState({ diffCurrentPwd: false, diffCurrentError: true });
      } else {
        this.setState({ diffCurrentPwd: true, diffCurrentError: false });
      }
    }
    this.setState({ oldPassword, oldPwdErrMsg });
  }
  onNewPasswordChange(e) {
    const { oldPassword } = this.state;
    const newPassword = e.target.value;
    let geMinLength = false;
    if (newPassword.length >= PWD_MIN_LENGTH) {
      geMinLength = true;
    }
    let diffCurrentPwd = false;
    if (newPassword !== oldPassword) {
      diffCurrentPwd = true;
    }
    const containNumber = isContainNumber(newPassword);
    const containUpperCase = isContainUpperCase(newPassword);
    const containLowerCase = isContainLowerCase(newPassword);
    const containSpecialChar = isContainSpecialChar(newPassword);

    let geMinLengthError = false;
    if (!geMinLength) {
      geMinLengthError = true;
    }
    let diffCurrentError = false;
    if (!diffCurrentPwd) {
      diffCurrentError = true;
    }
    let containError = false;
    let containNum = 0; // 包含数字，大小写，特殊字符的数量。
    [containNumber, containUpperCase, containLowerCase, containSpecialChar].forEach((item) => {
      if (item) {
        containNum++;
      }
    });
    if (containNum < 3) {
      // 至少包含数字，大写，小写，特殊字符中的三种。
      containError = true;
    }

    this.setState({
      newPassword,
      confirmPwdErrMsg: '',
      geMinLength,
      diffCurrentPwd,
      containNumber,
      containUpperCase,
      containLowerCase,
      containSpecialChar,
      geMinLengthError,
      diffCurrentError,
      containError,
    });
  }
  onConfirmPasswordChange(e) {
    const confirmPassword = e.target.value;
    this.setState({ confirmPassword, confirmPwdErrMsg: '' });
  }
  showStrengthCheck() {
    this.setState({ showStrengthCheck: true });
  }
  onCancel() {
    this.setState(initState);
    this.props.onCancel();
  }
  onSubmit() {
    const { updatePassword } = this.props;
    const { oldPassword, newPassword, confirmPassword } = this.state;
    if (!oldPassword) {
      return this.setState({ oldPwdErrMsg: i18n.t`原始密码不能为空` });
    }
    if (newPassword !== confirmPassword) {
      return this.setState({
        confirmPwdErrMsg: i18n.t`两次密码不一致`,
      });
    }
    updatePassword(oldPassword, newPassword, (err) => {
      if (err) {
        if (err === '原密码错误') {
          this.setState({ oldPwdErrMsg: i18n.t`原密码错误` });
        } else {
          // eslint-disable-next-line react/no-unused-state
          this.setState({ errorMessage: err });
        }
      } else {
        this.onCancel();
        // eslint-disable-next-line @moka-fe/lingui-mark
        document.body.innerHTML += `
          <div id="offline-mask">
            <div class="box">
              <div class="title">${i18n.t`下线通知`}</div>
              <div class="sub-title">${i18n.t`账号密码已修改，请重新登录`}</div>
              <form action="/api/headhunters/logout" method="POST">
                <input type="submit" value=${i18n.t`确认`} class="submit">
              </form>
            </div>
          </div>
          `;
      }
    });
  }
  renderStrengthCheck(pwdIsOk) {
    const {
      showStrengthCheck,
      geMinLength,
      geMinLengthError,
      containNumber,
      containUpperCase,
      containLowerCase,
      containSpecialChar,
      containError,
      diffCurrentPwd,
      diffCurrentError,
    } = this.state;
    if (!showStrengthCheck) {
      return null;
    }
    if (pwdIsOk) {
      return (
        <div className={styles.pwdSuccess}>
          <Icon name="success" className={styles.icon} />
          <span>{i18n.t`可以使用该密码`}</span>
        </div>
      );
    }
    return (
      <div className={styles.strengthCheck}>
        <div
          className={
            geMinLength ? styles.itemPositive : geMinLengthError ? styles.itemError : styles.item
          }
        >
          <span className={styles.point} />
          <span>{i18n.t`至少为8位字符`}</span>
        </div>
        <div
          className={
            containUpperCase ? styles.itemPositive : containError ? styles.itemError : styles.item
          }
        >
          <span className={styles.point} />
          <span>{i18n.t`包含大写字母`}</span>
        </div>
        <div
          className={
            containLowerCase ? styles.itemPositive : containError ? styles.itemError : styles.item
          }
        >
          <span className={styles.point} />
          <span>{i18n.t`包含小写字母`}</span>
        </div>
        <div
          className={
            containNumber ? styles.itemPositive : containError ? styles.itemError : styles.item
          }
        >
          <span className={styles.point} />
          <span>{i18n.t`包含数字`}</span>
        </div>
        <div
          className={
            containSpecialChar ? styles.itemPositive : containError ? styles.itemError : styles.item
          }
        >
          <span className={styles.point} />
          <span>{i18n.t`包含特殊字符`}</span>
        </div>
        <div
          className={
            diffCurrentPwd ? styles.itemPositive : diffCurrentError ? styles.itemError : styles.item
          }
        >
          <span className={styles.point} />
          <span>{i18n.t`不能与当前密码一致`}</span>
        </div>
      </div>
    );
  }
  render() {
    const { isOpen } = this.props;
    const {
      oldPassword,
      newPassword,
      confirmPassword,
      isSavingPassword,
      oldPwdErrMsg,
      confirmPwdErrMsg,
      geMinLengthError,
      containError,
      diffCurrentError,
    } = this.state;
    let pwdIsOk = false;
    if (newPassword) {
      pwdIsOk = !geMinLengthError && !containError && !diffCurrentError;
    }
    const canSubmit = pwdIsOk && oldPassword && newPassword && confirmPassword;

    return (
      <Modal
        isOpen={isOpen}
        title={i18n.t`更改密码`}
        isConfirmDisabled={!isSavingPassword && !canSubmit}
        submitText={i18n.t`确定`}
        closeOnConfirm={false}
        onClose={this.onCancel}
        onConfirm={this.onSubmit}
      >
        <Form.Item spacing="md" label={i18n.t`当前密码`} message={oldPwdErrMsg}>
          <Input
            type="password"
            value={oldPassword}
            placeholder={i18n.t`输入当前密码`}
            onChange={this.onOldPasswordChange}
          />
        </Form.Item>
        <Form.Item spacing="md" label={i18n.t`新密码`}>
          <Input
            type="password"
            value={newPassword}
            placeholder={i18n.t`输入新密码`}
            onChange={this.onNewPasswordChange}
            onFocus={this.showStrengthCheck}
          />
        </Form.Item>
        {this.renderStrengthCheck(pwdIsOk)}
        <Form.Item spacing="md" label={i18n.t`确认新密码`} message={confirmPwdErrMsg}>
          <Input
            type="password"
            value={confirmPassword}
            placeholder={i18n.t`确认新密码`}
            onChange={this.onConfirmPasswordChange}
          />
        </Form.Item>
      </Modal>
    );
  }
}

ChangePassword.propTypes = {
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  updatePassword: PropTypes.func,
};

export default ChangePassword;
